import axios from "axios";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface WithdrawalItem {
  uid: string;
  transaction_id: number;
  transaction_date: string;
  name: string;
  amount: number;
  status: string;
  utr_no: string; // Added to match the new data structure
  bank: object;
}

const ManualWithdrawal = () => {
  const [isManualPayment, setIsManualPayment] = useState(true); // To toggle between manual and online withdrawals
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalItem[]>([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(5); // or make it dynamic if needed
  const [totalPages, setTotalPages] = useState(1);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    fetchWithdrawalData(page, limit); // Fetch data when component mounts or page changes
  }, [page, isManualPayment]); // Re-fetch data when payment type changes

  // Fetch withdrawal data from the API based on transaction type

  const sendWarning = (transaction) => {
    setSelectedTransaction(transaction);
    setWarningMessage(transaction.warning_message || ""); // preload if any
    setIsWarningModalOpen(true);
  };
  const fetchWithdrawalData = async (page, limit) => {
    try {
      const transactionType = isManualPayment ? "manual" : "online"; // Set transaction type based on state
      const response = await axios.get(
        `https://api.trubetx.com/getManualWithdrawalData?page=${page}&limit=${limit}`,
        {
          params: { transactionType }, // Pass transactionType to the backend
        }
      );
      const data = response.data?.data;

      setTotalPages(response?.data?.totalPages || 1); // Set total pages from response
      setPage(response?.data?.currentPage || 1); // Set current page from response
      // Check if data is an array

      if (Array.isArray(data)) {
        const reversedData = data.reverse(); // Optionally reverse data for most recent first
        setWithdrawalData(reversedData);
      } else {
        console.error("Unexpected response structure:", data);
        setWithdrawalData([]);
      }
    } catch (error) {
      console.error("Failed to fetch withdrawal data:", error);
      setWithdrawalData([]);
    }
  };

  const handleManualPaymentToggle = () => {
    setIsManualPayment(true);
  };

  const handleOnlinePaymentToggle = () => {
    setIsManualPayment(false);
  };

  const handleAcceptPayment = async (
    transactionId: number,
    utrNo: string,
    uid: string,
    amount: number,
    coin: string
  ) => {
    // Call the API to approve the withdrawal
    await approveOrRejectManualWithdrawal(utrNo, uid, coin, amount, true);
    console.log(`Accepted withdrawal for transaction ID: ${transactionId}`);
  };

  const handleRejectPayment = async (
    transactionId: number,
    utrNo: string,
    uid: string,
    amount: number,
    coin: string
  ) => {
    // Call the API to reject the withdrawal
    await approveOrRejectManualWithdrawal(utrNo, uid, coin, amount, false);
    console.log(`Rejected withdrawal for transaction ID: ${transactionId}`);
  };

  const approveOrRejectManualWithdrawal = async (
    utr_no: string,
    uid: string,
    coin: string,
    amount: number,
    isApproved: boolean
  ) => {
    try {
      await axios.post(
        "https://api.trubetx.com/approveRejectManualWithdrawal",
        {
          utr_no,
          uid,
          coin,
          amount,
          isApproved,
        }
      );
      // Optionally refresh withdrawal data after processing
      fetchWithdrawalData(page, limit);
    } catch (error) {
      console.error("Error processing manual withdrawal:", error);
    }
  };

  const [selectedBank, setSelectedBank] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);

  const handleViewBank = (bankData) => {
    setSelectedBank(bankData);
    setShowBankModal(true);
  };

  const closeBankModal = () => {
    setShowBankModal(false);
    setSelectedBank(null);
  };

  return (
    <div className="ml-0 lg:ml-[256px]">
      <div className="flex gap-4 mt-4">
        <button
          className={`px-4 py-2 rounded ${
            isManualPayment ? "bg-blue-500 text-white" : "bg-gray-300"
          }`}
          onClick={handleManualPaymentToggle}
        >
          Manual Withdrawal
        </button>
        <button
          className={`px-4 py-2 rounded ${
            !isManualPayment ? "bg-blue-500 text-white" : "bg-gray-300"
          }`}
          onClick={handleOnlinePaymentToggle}
        >
          Online Withdrawal
        </button>
      </div>

      {/* Conditional Table */}
      <div className="mt-6">
        {isManualPayment ? (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-black">
              Manual Withdrawal Details
            </h2>
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-white uppercase bg-[#394a6a]">
                <tr>
                  <th className="px-4 py-2 border ">SNo.</th>
                  <th className="px-4 py-2 border ">User Name</th>
                  <th className="px-4 py-2 border ">Amount</th>
                  <th className="px-4 py-2 border ">UTR No</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Date</th>
                  <th className="px-4 py-2 border">Actions</th>
                  <th className="px-4 py-2 border">Message</th>
                  <th className="px-4 py-2 border">Send Warning</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {withdrawalData.map((withdrawal, index) => (
                  <tr key={withdrawal.transaction_id}>
                    <td className="px-4 py-2 border text-black">{index + 1}</td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.name}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      ${withdrawal.amount.toFixed(1)}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.bank == null
                        ? withdrawal?.utr_no
                        : withdrawal.bank && (
                            <button
                              className="px-3 py-1 mb-2 bg-green-600 text-white rounded"
                              onClick={() => handleViewBank(withdrawal.bank)}
                            >
                              View Bank
                            </button>
                          )}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.status}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {new Date(withdrawal.transaction_date).toLocaleString()}
                    </td>

                    <td className="px-4 py-2 border text-black">
                      {withdrawal.status === "pending" ? (
                        <>
                          <button
                            className="px-3 py-1 mr-2 bg-[#228BE6] text-white rounded"
                            onClick={() =>
                              handleAcceptPayment(
                                withdrawal.transaction_id,
                                withdrawal.utr_no,
                                withdrawal.uid,
                                withdrawal.amount,
                                "inr"
                              )
                            }
                          >
                            Accept
                          </button>
                          <button
                            className="px-3 py-1 bg-red-500 text-white rounded"
                            onClick={() =>
                              handleRejectPayment(
                                withdrawal.transaction_id,
                                withdrawal.utr_no,
                                withdrawal.uid,
                                withdrawal.amount,
                                "inr"
                              )
                            }
                          >
                            Reject
                          </button>
                        </>
                      ) : withdrawal.status === "accepted" ? (
                        <button className="px-3 py-1 mr-2 bg-[#228BE6] text-white rounded">
                          Accepted
                        </button>
                      ) : (
                        <button className="px-3 py-1 mr-2 bg-red-500 text-white rounded">
                          Rejected
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2 border text-black text-nowrap">
                      {withdrawal?.warning_message}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      <button
                        className="px-3 py-1 mr-2 bg-red-500 text-white rounded"
                        onClick={() => sendWarning(withdrawal)}
                      >
                        Send Warning
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={16} className="py-4 px-6 text-start">
                    <nav className="inline-flex items-center space-x-2">
                      <button
                        onClick={() => fetchWithdrawalData(page - 1, limit)}
                        disabled={page <= 1}
                        className={`px-4 py-2 rounded ${
                          page <= 1
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                        }`}
                      >
                        Previous
                      </button>
                      <span className="text-gray-700 px-4 py-2">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => fetchWithdrawalData(page + 1, limit)}
                        disabled={page >= totalPages}
                        className={`px-4 py-2 rounded ${
                          page >= totalPages
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                        }`}
                      >
                        Next
                      </button>
                    </nav>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-black">
              Online Withdrawal Details
            </h2>
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-white uppercase bg-[#394a6a]">
                <tr>
                  <th className="px-4 py-2 border">SNo.</th>
                  <th className="px-4 py-2 border">User Name</th>
                  <th className="px-4 py-2 border">Amount</th>
                  <th className="px-4 py-2 border">UTR No</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Date</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {withdrawalData.map((withdrawal, index) => (
                  <tr key={withdrawal.transaction_id}>
                    <td className="px-4 py-2 border text-black">
                      {index + 1}asd
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.name}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      ${withdrawal.amount.toFixed(1)}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.utr_no}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.status}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {new Date(withdrawal.transaction_date).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 border text-black">
                      {withdrawal.status === "pending" ? (
                        <>
                          <button
                            className="px-3 py-1 mr-2 bg-[#228BE6] text-white rounded"
                            onClick={() =>
                              handleAcceptPayment(
                                withdrawal.transaction_id,
                                withdrawal.utr_no,
                                withdrawal.uid,
                                withdrawal.amount,
                                "inr"
                              )
                            }
                          >
                            Accept
                          </button>
                          <button
                            className="px-3 py-1 bg-red-500 text-white rounded"
                            onClick={() =>
                              handleRejectPayment(
                                withdrawal.transaction_id,
                                withdrawal.utr_no,
                                withdrawal.uid,
                                withdrawal.amount,
                                "inr"
                              )
                            }
                          >
                            Reject
                          </button>
                        </>
                      ) : withdrawal.status === "accepted" ? (
                        <button className="px-3 py-1 mr-2 bg-[#228BE6] text-white rounded">
                          Accepted
                        </button>
                      ) : (
                        <button className="px-3 py-1 mr-2 bg-red-500 text-white rounded">
                          Rejected
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={16} className="py-4 px-6 text-start">
                    <nav className="inline-flex items-center space-x-2">
                      <button
                        onClick={() => fetchWithdrawalData(page - 1, limit)}
                        disabled={page <= 1}
                        className={`px-4 py-2 rounded ${
                          page <= 1
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                        }`}
                      >
                        Previous
                      </button>
                      <span className="text-gray-700 px-4 py-2">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => fetchWithdrawalData(page + 1, limit)}
                        disabled={page >= totalPages}
                        className={`px-4 py-2 rounded ${
                          page >= totalPages
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                        }`}
                      >
                        Next
                      </button>
                    </nav>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {isWarningModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
      <h2 className="text-lg font-semibold mb-4 text-black">Send Warning</h2>
      <input
        type="text"
        className="w-full p-2 border border-gray-300 rounded mb-4 text-black"
        placeholder="Enter warning message"
        value={warningMessage}
        onChange={(e) => setWarningMessage(e.target.value)}
      />
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setIsWarningModalOpen(false)}
          className="bg-gray-300 text-black px-4 py-2 rounded"
        >
          Cancel
        </button>
        <button
          onClick={async () => {
            try {
              await axios.post("https://api.trubetx.com/updateWithdrawWarningMessage", {
                utr_no: selectedTransaction?.utr_no,
                uid: selectedTransaction?.uid,
                warning_message: warningMessage,
              });
              alert("Warning message updated!");
              setIsWarningModalOpen(false);
              fetchWithdrawalData(page, limit); // refresh
            } catch (err) {
              console.error(err);
              alert("Failed to update warning.");
            }
          }}
          className="bg-red-600 text-white px-4 py-2 rounded"
        >
          Submit
        </button>
      </div>
    </div>
  </div>
)}


      <AnimatePresence>
        {showBankModal && selectedBank && (
          <motion.div
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-6 rounded shadow-xl max-w-md w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                Bank Details
              </h3>
              <p>
                <strong>Holder Name:</strong> {selectedBank.account_holder_name}
              </p>
              <p>
                <strong>Account Number:</strong> {selectedBank.account_number}
              </p>
              <p>
                <strong>Bank Name:</strong> {selectedBank.bank_name}
              </p>
              <p>
                <strong>IFSC Code:</strong> {selectedBank.ifsc_code}
              </p>
              <p>
                <strong>Phone No:</strong> {selectedBank.phone_no}
              </p>
              <div className="mt-4 text-right">
                <button
                  onClick={closeBankModal}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManualWithdrawal;
